import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useTranslation } from "react-i18next";
import { format, parse } from "date-fns";
import { t } from "i18next";
import { DatePicker } from "@/components/DatePicker";
import { ScreenSizeSelect } from "@/pages/screenSize/ScreenSizeSelect";

const formSchema = z
  .object({
    name: z.string().min(3),
    screenSizeId: z.number(),
    layoutConfig: z.string(),
    startDate: z.string(),
    endDate: z.string(),
  })
  .refine(
    (data) => {
      if (!data.startDate || !data.endDate) return true;
      const start = parse(data.startDate, "dd-MM-yyyy HH:mm", new Date());
      const end = parse(data.endDate, "dd-MM-yyyy HH:mm", new Date());
      return end >= start;
    },
    {
      message: t("common.endDateMustBeAfterStartDate"),
      path: ["endDate"],
    }
  );

export type FormSchemaType = z.infer<typeof formSchema>;

type Props = {
  buttonText: string;
  onSubmit: (values: FormSchemaType) => void;
  isLoading: boolean;
};

export const SplitContentMetaForm: React.FC<Props> = ({ buttonText, onSubmit, isLoading }) => {
  const { t } = useTranslation();
  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      startDate: format(new Date(), "dd-MM-yyyy HH:mm"),
      layoutConfig: JSON.stringify({ id: "root", type: "content" }),
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("common.name")}</FormLabel>
              <FormControl>
                <Input placeholder={t("common.name")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="screenSizeId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("entities.screenSize")}</FormLabel>
              <FormControl>
                <ScreenSizeSelect field={field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="startDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("common.startDate")}
              </FormLabel>
              <FormControl>
                <div>
                  <DatePicker value={field.value} onChange={field.onChange} withTime />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="endDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("common.endDate")}
              </FormLabel>
              <FormControl>
                <div>
                  <DatePicker value={field.value} onChange={field.onChange} withTime />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={isLoading}>
          {buttonText}
          {isLoading && <LoadingSmall />}
        </Button>
      </form>
    </Form>
  );
};
